/* eslint-disable react/jsx-props-no-spreading */
/**
 * UserManagementAssignmentModal Component
 */

import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'

import type {
  AssignmentViewModel,
  UserAssignmentsViewModel,
} from '@rsmus/ecp-userservice'

import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import EmailIcon from '../../rsmCoreComponents/components/icons/EmailIcon'
import { tokens } from '../../styles/materialTheme'
import UserAssignment from './UserAssignment'
import UserManagementConfirmRevokeAll from './UserManagementConfirmRevokeAll'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert/CustomSuccessAlert'
import CustomErrorAlert from '../forms/Alert/CustomErrorAlert/CustomErrorAlert'

export interface UserManagementAssignmentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onError: () => void
  userEmail: string
  firstName?: string
  lastName?: string
}

interface UserProfile {
  name?: string
  email?: string
  jobTitle?: string
  companyName?: string
  status?: string
  lastLogin?: Date | string
}

const styles: Styles = {
  modalContainer: {
    padding: '2rem',
    width: '100%',
    maxWidth: '60rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  headerRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  header: {
    fontFamily: 'Prelo-Bold',
    fontSize: '1.5rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    width: '100%',
  },
  userInfoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  userEmailRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  },
  userInfoText: {
    fontFamily: 'Prelo',
    fontSize: '1rem',
    color: tokens.colors.rsmGray.copy,
    marginBottom: '0.25rem',
  },
  userNameText: {
    fontFamily: 'Prelo-Bold',
    fontSize: '1rem',
    color: tokens.colors.rsmGray.copy,
    marginBottom: '0.5rem',
  },
  assignmentSection: {
    width: '100%',
    marginBottom: '1.5rem',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '1rem',
    width: '100%',
  },
  errorBox: {
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: tokens.colors.warning.secondary,
    padding: '1rem',
    borderRadius: '0.25rem',
    width: '100%',
    boxSizing: 'border-box',
  },
}

const formatDisplayName = (
  firstName?: string,
  lastName?: string,
  fallbackName?: string,
): string | undefined => {
  const trimmedFirst = firstName?.trim()
  const trimmedLast = lastName?.trim()

  if (trimmedFirst && trimmedLast) return `${trimmedLast}, ${trimmedFirst}`
  if (trimmedLast) return trimmedLast
  if (trimmedFirst) return trimmedFirst

  return fallbackName
}

const UserManagementAssignmentModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  userEmail,
  firstName,
  lastName,
}: UserManagementAssignmentModalProps) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [assignments, setAssignments] = useState<AssignmentViewModel[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [openRevokeDialog, setOpenRevokeDialog] = useState(false)
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [showAssignmentValidation, setShowAssignmentValidation] =
    useState(false)
  const [areAssignmentsValid, setAreAssignmentsValid] = useState(false)

  const methods = useForm({
    defaultValues: {},
  })

  const resolvedFirstName = firstName?.trim() ?? ''
  const resolvedLastName = lastName?.trim() ?? ''
  const displayName = formatDisplayName(
    resolvedFirstName,
    resolvedLastName,
    userProfile?.name,
  )

  // Fetch profile on open
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!open || !userEmail) return

      try {
        setIsLoading(true)
        setError(null)

        const profileResponse =
          await api.user.clientAdmin_GetUserProfile(userEmail)

        if (profileResponse?.data) {
          setUserProfile(profileResponse.data)
        }
      } catch (err: unknown) {
        const msg = t('UserManagement.ManageUser.ErrorFetchingProfile')
        setError(msg)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserProfile()
  }, [open, userEmail, t])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setIsLoading(true)
      setError(null)
      setUserProfile(null)
      setAssignments([])
      setIsSubmitting(false)
      setOpenRevokeDialog(false)
      setShowSuccessToast(false)
      setShowErrorToast(false)
      setShowAssignmentValidation(false)
      setAreAssignmentsValid(false)
    }
  }, [open])

  const handleAssignmentsChange = (newAssignments: AssignmentViewModel[]) => {
    setAssignments(newAssignments)
  }

  // ---------------------------
  // UPDATE USER HANDLER
  // ---------------------------
  const handleUpdate = async () => {
    setShowAssignmentValidation(true)

    if (!areAssignmentsValid) return

    setIsSubmitting(true)

    try {
      const { jobTitle } = userProfile ?? {}

      const model: UserAssignmentsViewModel = {
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        email: userEmail,
        jobTitle: jobTitle ?? '',
        assignments,
      }

      await api.user.clientAdmin_UpdateUserAssignments(model)

      setShowSuccessToast(true)
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const msg = t('UserManagement.ManageUser.ErrorMessage')
      setError(msg)
      setShowErrorToast(true)
      onError()
    } finally {
      setIsSubmitting(false)
    }
  }

  // ---------------------------
  // REVOKE ALL HANDLER
  // ---------------------------
  const handleRevokeConfirm = async () => {
    setIsSubmitting(true)
    setOpenRevokeDialog(false)

    try {
      const { jobTitle } = userProfile ?? {}

      const model: UserAssignmentsViewModel = {
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        email: userEmail,
        jobTitle: jobTitle ?? '',
        assignments: [], // revoke all
      }

      await api.user.clientAdmin_UpdateUserAssignments(model)

      setShowSuccessToast(true)
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const msg = t('UserManagement.RevokeAccess.ErrorMessage')
      setError(msg)
      setShowErrorToast(true)
      onError()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => onClose()
  const handleRevokeClick = () => setOpenRevokeDialog(true)

  // Keep component mounted while the revoke dialog is open so the
  // confirmation dialog can show even after the main modal closes.
  if (!open && !openRevokeDialog) return null

  return (
    <>
      <ResponsiveModal
        open={open && !openRevokeDialog}
        onClose={handleCancel}
        desktopMaxWidth="60rem"
        aria-labelledby="manage-user-modal-title">
        <Box sx={styles.modalContainer}>
          <Box sx={styles.headerRow}>
            <Typography
              sx={styles.header}
              data-testid="Lbl_ManageUserModal_Header">
              {t('UserManagement.ManageUser.Title')}
            </Typography>

            {!isLoading && !error && userProfile && (
              <Box sx={styles.userInfoSection} data-testid="Div_UserInfo">
                {displayName && (
                  <Typography sx={styles.userNameText}>
                    {displayName}
                  </Typography>
                )}
                {userProfile.jobTitle && (
                  <Typography sx={styles.userInfoText}>
                    {userProfile.jobTitle}
                  </Typography>
                )}
                <Box sx={styles.userEmailRow}>
                  <EmailIcon />
                  <Typography sx={styles.userInfoText}>
                    {userProfile.email ?? userEmail}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {isLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={styles.errorBox} role="alert">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {!isLoading && !error && userProfile && (
            <>
              <Box sx={styles.assignmentSection}>
                <FormProvider {...methods}>
                  <UserAssignment
                    userEmail={userEmail}
                    onChange={handleAssignmentsChange}
                    showValidationErrors={showAssignmentValidation}
                    onValidityChange={(isValid) =>
                      setAreAssignmentsValid(isValid)
                    }
                  />
                </FormProvider>
              </Box>

              <Box sx={styles.buttonContainer}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  data-testid="Btn_ManageUser_Cancel">
                  {t('UserManagement.ManageUser.CancelButton')}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleRevokeClick}
                  disabled={isSubmitting}
                  data-testid="Btn_ManageUser_Revoke">
                  {t('UserManagement.ManageUser.RevokeButton')}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={isSubmitting}
                  data-testid="Btn_ManageUser_Update">
                  {t('UserManagement.ManageUser.UpdateButton')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </ResponsiveModal>

      <UserManagementConfirmRevokeAll
        open={openRevokeDialog}
        onClose={() => setOpenRevokeDialog(false)}
        onConfirm={handleRevokeConfirm}
        userFirstName={resolvedFirstName}
        userLastName={resolvedLastName}
        userEmail={userEmail}
      />

      <CustomSuccessAlert
        header="UserManagement.ManageUser.SuccessHeader"
        message="UserManagement.ManageUser.SuccessMessage"
        open={showSuccessToast}
        close={() => setShowSuccessToast(false)}
        testId="Alert_ManageUser_Success"
      />

      <CustomErrorAlert
        header={t('UserManagement.ManageUser.ErrorHeader')}
        message={t('UserManagement.ManageUser.ErrorMessage')}
        open={showErrorToast}
        close={() => setShowErrorToast(false)}
        testId="Alert_ManageUser_Error"
      />
    </>
  )
}

export default UserManagementAssignmentModal
